import { Star } from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { IgAnalyticsTooltip, IgTypography } from "@intelligems/ig-theme";
import { STANDARD_EVENTS_ALLOWED_KEY_METRICS } from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { findTooltip } from "@src/features/analytics/baseDashboard/utils";
import { chartTooltips } from "@src/features/analytics/utils";
import { getPrimaryMetricId } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";

interface HeaderDetailsProps {
  config: GraphConfig;
  metricFilterIndex?: number;
}

export const HeaderDetails = ({
  config,
  metricFilterIndex,
}: HeaderDetailsProps) => {
  const theme = useTheme();
  const experience = useSelector(selectedExperience);

  const title = config?.title?.[metricFilterIndex || 0] ?? config?.title?.[0];
  const primaryMetricId = useSelector(getPrimaryMetricId);

  const showPrimaryStar = useMemo(() => {
    if (!experience || experience.category === "personalization") return false;
    const metricToUse = config?.impactMetric ?? config?.metrics[0];
    return (
      (STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryMetricId]
        ?.analyticsMetricId || primaryMetricId) === metricToUse
    );
  }, []);

  const TooltipTitle = (
    <IgTypography>
      <IgTypography fontWeight={600}>{title}</IgTypography>
      <br />
      {config.titleTooltip ||
        findTooltip(
          config,
          chartTooltips,
          experience?.category ?? "experiment",
          metricFilterIndex
        )}
    </IgTypography>
  );

  return (
    <Stack direction={"row"} alignItems={"center"} gap={1}>
      {showPrimaryStar && (
        <IgAnalyticsTooltip title={"Primary Goal"}>
          <Star size={18} color={theme.igPrimitives.yellow[500]} />
        </IgAnalyticsTooltip>
      )}

      {title && (
        <IgAnalyticsTooltip title={TooltipTitle}>
          <IgTypography fontWeight={600} variant="h3">
            {title}
          </IgTypography>
        </IgAnalyticsTooltip>
      )}
    </Stack>
  );
};
